import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Button,
  Stack,
  Chip,
  Paper,
} from '@mui/material';
import {
  Lock as LockIcon,
  Upgrade as UpgradeIcon,
  TrendingUp as TrendingUpIcon,
  Assessment as AssessmentIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { apiClient, DashboardAnalytics, BillingMeResponse } from '../../services/api';

export function Analytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [billing, setBilling] = useState<BillingMeResponse | null>(null);
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        setError(null);
        setLoading(true);

        // Fetch user's billing/plan information
        const billingData = await apiClient.getMyBilling();
        if (!isMounted) return;
        setBilling(billingData);

        // Check if user is on Free plan
        const planId = billingData.plan?.id?.toLowerCase();
        const locked = planId === 'free';
        setIsLocked(locked);

        // Only fetch analytics if not locked
        if (!locked) {
          const analyticsData = await apiClient.getDashboardAnalytics();
          if (!isMounted) return;
          setAnalytics(analyticsData);
        }
      } catch (e: any) {
        if (!isMounted) return;
        console.error('Error loading analytics:', e);
        setError(e?.response?.data?.message || e.message || 'Failed to load analytics');
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Analytics
        </Typography>
        <Card>
          <CardContent>
            <Typography color="error">{error}</Typography>
            <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>
              Retry
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Show locked state for Free plan users
  if (isLocked) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Analytics
        </Typography>
        
        <Card
          sx={{
            position: 'relative',
            minHeight: '500px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            overflow: 'hidden',
          }}
        >
          {/* Blurred background pattern */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.1,
              background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255,255,255,.05) 10px, rgba(255,255,255,.05) 20px)',
            }}
          />

          <CardContent
            sx={{
              position: 'relative',
              zIndex: 1,
              textAlign: 'center',
              maxWidth: '600px',
              mx: 'auto',
              py: 6,
            }}
          >
            {/* Lock Icon */}
            <Box
              sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                mb: 3,
              }}
            >
              <LockIcon sx={{ fontSize: 60, color: 'white' }} />
            </Box>

            {/* Title */}
            <Typography variant="h4" gutterBottom fontWeight="bold">
              Advanced Analytics Locked
            </Typography>

            {/* Subtitle */}
            <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
              Not Available in Free Plan
            </Typography>

            {/* Description */}
            <Typography variant="body1" sx={{ mb: 4, opacity: 0.85, lineHeight: 1.7 }}>
              Unlock powerful insights into your security compliance with advanced analytics,
              trend analysis, and detailed compliance reports. Upgrade your plan to access
              comprehensive dashboards and data visualizations.
            </Typography>

            {/* Features List */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.15)',
                backdropFilter: 'blur(10px)',
                p: 3,
                mb: 4,
                borderRadius: 2,
              }}
            >
              <Stack spacing={2} alignItems="flex-start">
                <Box display="flex" alignItems="center" gap={2}>
                  <TrendingUpIcon />
                  <Typography variant="body2" textAlign="left">
                    Real-time trend analysis and historical comparisons
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <AssessmentIcon />
                  <Typography variant="body2" textAlign="left">
                    Detailed compliance score breakdowns by framework
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={2}>
                  <SecurityIcon />
                  <Typography variant="body2" textAlign="left">
                    Risk score tracking and severity distribution charts
                  </Typography>
                </Box>
              </Stack>
            </Paper>

            {/* Current Plan Badge */}
            <Chip
              label={`Current Plan: ${billing?.plan?.name || 'Free'}`}
              sx={{
                mb: 3,
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                color: 'white',
                fontWeight: 'bold',
              }}
            />

            {/* Upgrade Button */}
            <Button
              variant="contained"
              size="large"
              startIcon={<UpgradeIcon />}
              onClick={() => navigate('/pricing')}
              sx={{
                bgcolor: 'white',
                color: '#667eea',
                fontWeight: 'bold',
                px: 4,
                py: 1.5,
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.9)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Upgrade to Unlock Analytics
            </Button>

            {/* Additional Info */}
            <Typography variant="caption" sx={{ mt: 3, display: 'block', opacity: 0.7 }}>
              Available in Starter, Pro, and Enterprise plans
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Show actual analytics for paid plan users
  if (!analytics) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Analytics
        </Typography>
        <Card>
          <CardContent>
            <Typography>No analytics data available</Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // Render actual analytics
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Analytics Dashboard
      </Typography>

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Scans
              </Typography>
              <Typography variant="h4">{analytics.summary.totalScans}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Issues
              </Typography>
              <Typography variant="h4">{analytics.summary.totalIssues}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Avg Risk Score
              </Typography>
              <Typography variant="h4">{analytics.summary.avgRiskScore}</Typography>
            </CardContent>
          </Card>
        </Box>

        <Box sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Max Risk Score
              </Typography>
              <Typography variant="h4">{analytics.summary.maxRiskScore}</Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Severity and Framework Breakdown */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        {/* Severity Breakdown */}
        <Box sx={{ flex: '1 1 400px', minWidth: 350 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Severity Breakdown
              </Typography>
              <Stack spacing={1}>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Critical:</Typography>
                  <Chip
                    label={analytics.severityBreakdown.critical}
                    color="error"
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>High:</Typography>
                  <Chip
                    label={analytics.severityBreakdown.high}
                    color="warning"
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Medium:</Typography>
                  <Chip label={analytics.severityBreakdown.medium} size="small" />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography>Low:</Typography>
                  <Chip label={analytics.severityBreakdown.low} size="small" />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Framework Breakdown */}
        <Box sx={{ flex: '1 1 400px', minWidth: 350 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Framework Breakdown
              </Typography>
              <Stack spacing={1}>
                {Object.entries(analytics.frameworkBreakdown).map(([framework, count]) => (
                  <Box key={framework} display="flex" justifyContent="space-between">
                    <Typography>{framework}:</Typography>
                    <Chip label={count} size="small" color="primary" />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Recent Scans */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Recent Scans
          </Typography>
          <Stack spacing={2}>
            {analytics.recentScans.map((scan) => (
              <Paper
                key={scan.scanId}
                elevation={1}
                sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              >
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {scan.scanId}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(scan.timestamp).toLocaleString()}
                  </Typography>
                </Box>
                <Box display="flex" gap={2}>
                  <Box textAlign="center">
                    <Typography variant="caption" color="text.secondary">
                      Risk Score
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {scan.riskScore}
                    </Typography>
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="caption" color="text.secondary">
                      Issues
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {scan.totalIssues}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

